'use client';

import {AuthForm} from 'iq-auth/components/auth_form';
import {useSignupCta} from 'iq-auth/hooks';
import {SubmitExperienceModal} from 'iq-interview_experiences/components/submit_experience_modal';
import {GateOverlay} from 'iq-interview_guides/components/company_position_guides/gate_overlay';
import {trackGuidePaywallViewed, trackGuideSignupClicked} from 'iq-interview_guides/tracking';
import {SignupUnlockGuideModalContent} from 'iq-upselling/components/signup_unlock_guide_modal_content';
import {UnlockInterviewGuideModalContent} from 'iq-upselling/components/unlock_interview_guide_modal_content';
import {useEffect, useState} from 'react';

type GateMode = 'public' | 'free';

interface LockedGuideGateProps {
  gateMode: GateMode;
  companySlug: string;
  positionSlug: string;
  companyId: number;
  positionId: number;
  guideSlug: string;
}

interface GateCardProps {
  signupOpen: boolean;
  showSignupCta: boolean;
  onSignup: () => void;
  onSignupBack: () => void;
  onShare: () => void;
}

function GateCard({signupOpen, showSignupCta, onSignup, onSignupBack, onShare}: GateCardProps) {
  if (signupOpen) {
    return (
      <div className='relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8'>
        {/* AuthForm opens on its signup tab by default and offers its own switch to login. */}
        <AuthForm onSuccess={() => window.location.reload()} onBack={onSignupBack} />
      </div>
    );
  }
  if (showSignupCta) {
    return <SignupUnlockGuideModalContent onSignup={onSignup} onShare={onShare} />;
  }
  return <UnlockInterviewGuideModalContent onShare={onShare} />;
}

export function LockedGuideGate({
  gateMode,
  companySlug,
  positionSlug,
  companyId,
  positionId,
  guideSlug,
}: LockedGuideGateProps) {
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const startSignup = useSignupCta('locked_guide_gate');

  // Anonymous visitors get the signup-first card (shipped after guide_gate_signup_cta
  // roughly doubled signups off this surface). Logged-in free users cannot raise their
  // quota by signing up, so they keep the share/subscribe card.
  const showSignupCta = gateMode === 'public';

  useEffect(() => {
    void trackGuidePaywallViewed({gateMode, slug: guideSlug});
  }, [gateMode, guideSlug]);

  function openSignup() {
    void trackGuideSignupClicked({slug: guideSlug});
    startSignup(() => setSignupOpen(true));
  }

  return (
    <>
      {/* The backdrop stays mounted while the signup form or share modal is open. */}
      <GateOverlay>
        <GateCard
          signupOpen={signupOpen}
          showSignupCta={showSignupCta}
          onSignup={openSignup}
          onSignupBack={() => setSignupOpen(false)}
          onShare={() => setShareModalOpen(true)}
        />
      </GateOverlay>
      {shareModalOpen && (
        <SubmitExperienceModal
          guideSlug={guideSlug}
          onClose={() => setShareModalOpen(false)}
          defaultCompanySlug={companySlug}
          defaultPositionSlug={positionSlug}
          companyId={companyId}
          positionId={positionId}
        />
      )}
    </>
  );
}
